import { db } from '../db';
import type { WatchlistEntry } from '../types';

export interface WatchlistApi {
  list(): Promise<WatchlistEntry[]>;
  add(entry: Omit<WatchlistEntry, 'id'>): Promise<number>;
  update(id: number, patch: Partial<WatchlistEntry>): Promise<void>;
  remove(id: number): Promise<void>;
  getByAnilistId(anilistId: number): Promise<WatchlistEntry | undefined>;
  exportJson(): Promise<string>;
  importJson(json: string): Promise<number>;
  clear(): Promise<void>;
}

export function useWatchlist(): WatchlistApi {
  return {
    async list(): Promise<WatchlistEntry[]> {
      const all = await db.watchlist.toArray();
      return all.sort((a, b) => {
        const av = a.nextEpisodeAiringAt ?? Number.POSITIVE_INFINITY;
        const bv = b.nextEpisodeAiringAt ?? Number.POSITIVE_INFINITY;
        return av - bv;
      });
    },

    async add(entry) {
      const existing = await db.watchlist.where('anilistId').equals(entry.anilistId).first();
      if (existing?.id != null) {
        await db.watchlist.update(existing.id, entry);
        return existing.id;
      }
      return (await db.watchlist.add(entry as WatchlistEntry)) as number;
    },

    async update(id, patch) {
      await db.watchlist.update(id, patch);
    },

    async remove(id) {
      await db.watchlist.delete(id);
    },

    async getByAnilistId(anilistId) {
      return db.watchlist.where('anilistId').equals(anilistId).first();
    },

    async exportJson() {
      const data = await db.watchlist.toArray();
      return JSON.stringify({ version: 1, exportedAt: new Date().toISOString(), data }, null, 2);
    },

    async importJson(json) {
      const parsed = JSON.parse(json) as { data: WatchlistEntry[] };
      if (!Array.isArray(parsed.data)) {
        throw new Error('Invalid backup file');
      }
      let count = 0;
      await db.transaction('rw', db.watchlist, async () => {
        for (const entry of parsed.data) {
          const { id: _id, ...rest } = entry;
          const existing = await db.watchlist
            .where('anilistId')
            .equals(rest.anilistId)
            .first();
          if (existing?.id != null) {
            await db.watchlist.update(existing.id, rest);
          } else {
            await db.watchlist.add(rest as WatchlistEntry);
          }
          count++;
        }
      });
      return count;
    },

    async clear() {
      await db.watchlist.clear();
    },
  };
}
